/**
 * Expanded detail for a `rebook_confirm` feed root. Renders the external
 * rebooks (contract switch / drop) confirmed from ingested agent data during
 * one collector run, grouped by the confirming agent feed.
 *
 * A single-agent card lists that agent's confirmed bookings directly; a
 * multi-agent card renders one section per agent (count + realized savings)
 * with its booking drill-down. Each booking row reuses the commercial-digest
 * row (From → To contract, lane + dates, parties) so opportunities and
 * confirmations look consistent on the feed.
 */
import { useUserPreferences } from "@/context/UserPreferencesContext";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { usePortNames } from "@/hooks/usePortNames";
import { extractLaneLocodes } from "@/lib/formatPort";
import { SensitiveMoney } from "@/components/common/Sensitive";
import type {
  RebookConfirmAgentGroup,
  RebookConfirmPayload,
} from "@/types/feed";
import {
  DigestBookingRow,
  useHydratedDigestBookings,
} from "./FeedCommercialDigestDetail";

/** Whether a payload is a `rebook_confirm` root. */
export function isRebookConfirmPayload(
  payload: unknown,
): payload is RebookConfirmPayload {
  if (!payload || typeof payload !== "object") return false;
  const p = payload as Record<string, unknown>;
  return p.kind === "rebook_confirm" && Array.isArray(p.agents);
}

function AgentBookings({ agent }: { agent: RebookConfirmAgentGroup }) {
  const { preferences } = useUserPreferences();
  const { formatDate } = useDateFormatter();
  const bookings = useHydratedDigestBookings(agent.bookings);
  const laneLocodes = extractLaneLocodes(...bookings.map((b) => b.lane_key));
  const portNames = usePortNames(laneLocodes);

  return (
    <div className="feed-digest__list">
      {bookings.map((booking) => (
        <DigestBookingRow
          key={booking.source_id}
          booking={booking}
          isRebook
          showLocodes={preferences.showLocodes}
          portNames={portNames}
          formatDate={formatDate}
        />
      ))}
    </div>
  );
}

export function FeedRebookConfirmDetail({
  payload,
}: {
  payload: RebookConfirmPayload;
}) {
  const total = payload.booking_count;
  const agents = payload.agents ?? [];
  const singleAgent = agents.length === 1;

  return (
    <div className="feed-digest feed-digest--rebook">
      <p className="feed-digest__summary feed-row-detail-body__heading">
        {total} rebook{total === 1 ? "" : "s"} confirmed
        {agents.length > 1 ? ` across ${agents.length} agents` : ""}
        {payload.total_savings_usd != null && payload.total_savings_usd > 0 ? (
          <>
            {" · "}
            <SensitiveMoney amount={payload.total_savings_usd} />
            {" realized"}
          </>
        ) : null}
      </p>

      {singleAgent ? (
        <AgentBookings agent={agents[0]} />
      ) : (
        <div className="feed-digest__groups">
          {agents.map((agent) => (
            <section key={agent.source} className="feed-digest__group">
              <header className="feed-digest__group-head">
                <span className="feed-digest__group-name">{agent.label}</span>
                <span className="feed-digest__group-meta">
                  {agent.bookings.length} rebook
                  {agent.bookings.length === 1 ? "" : "s"}
                  {agent.total_savings_usd != null &&
                  agent.total_savings_usd > 0 ? (
                    <>
                      {" · "}
                      <SensitiveMoney amount={agent.total_savings_usd} />
                    </>
                  ) : null}
                </span>
              </header>
              <AgentBookings agent={agent} />
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
